import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Loading from "../common/Loading";
import BillingAddressFields, { EMPTY_ADDRESS, addressFrom, addressPayload } from "../common/BillingAddressFields";
import { ACCENT, clean } from "../artistStudio/shared";
import { receiptLink } from "../receipts/receiptApi";
import FanMembershipShell from "./FanMembershipShell";
import { fanMembershipApi, recallArtist, isBillingPeriod, money, formatDate, apiErrorMessage, apiErrorCode } from "./fanMembershipApi";

// Checkout for one plan and billing period (?plan=premium&period=monthly). The server validates the plan and period and
// returns the price, taxes, total and renewal terms shown here. The card is charged through Stripe, and the membership
// becomes active only after the server has checked the payment with Stripe. In test environments the server also allows
// "Simulate: Success / Declined"; those buttons are shown only when the server says simulation is available.

const ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "16px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" },
    invalid: { color: "#BE1F2E" },
  },
};

const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between gap-4 py-2 ${strong ? "text-base font-bold text-gray-900 border-t border-gray-200 mt-1 pt-3" : "text-sm text-gray-700"}`}>
    <span>{label}</span>
    <span className="text-right">{value}</span>
  </div>
);

function Notice({ title, children, actions, tone = "error" }) {
  const styles = tone === "error" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div className={`rounded-2xl border px-4 py-3 ${styles}`} role="alert">
      <p className="font-semibold">{title}</p>
      <p className="text-sm mt-1">{children}</p>
      {actions && <div className="mt-3 flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  );
}

export default function FanMembershipCheckout() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [params] = useSearchParams();
  const planCode = params.get("plan") || "";
  const period = params.get("period") || "";
  const artistSlug = (params.get("artist") || "").toLowerCase() || recallArtist();
  const seenPrice = Number.isFinite(Number(params.get("price"))) && params.get("price") !== null && params.get("price") !== "" ? Number(params.get("price")) : null;

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [busy, setBusy] = useState(""); // "", "pay", "success" or "declined": one payment request at a time
  const [failure, setFailure] = useState("");
  const [waiting, setWaiting] = useState(null); // { intentId, message } payment sent but not confirmed yet
  const [done, setDone] = useState(null); // { message, subscription }
  const [priceNotice, setPriceNotice] = useState(null); // { from, to } the price changed: review it before paying
  const busyRef = useRef(false); // blocks a second click before React has re-rendered
  // Billing address for the sales tax. The tax is quoted again whenever a complete address changes; `quotedFor` is the address
  // the quote on screen was worked out for, so paying waits until the tax on screen matches the address typed.
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [showAddressErrors, setShowAddressErrors] = useState(false);
  const [quotedFor, setQuotedFor] = useState("");
  const prefilled = useRef(false);
  const addressKey = (a) => { const p = addressPayload(a); return p ? JSON.stringify(p) : ""; };

  const plansLink = (() => {
    const q = new URLSearchParams();
    if (isBillingPeriod(period)) q.set("period", period);
    if (planCode) q.set("plan", planCode);
    if (artistSlug) q.set("artist", artistSlug);
    const qs = q.toString();
    return `/fan-membership${qs ? `?${qs}` : ""}`;
  })();
  const withArtist = (body) => ({ ...body, ...(artistSlug ? { artist_slug: artistSlug } : {}) });

  const loadQuote = useCallback(async (expectedPrice, addr) => {
    if (!planCode || !isBillingPeriod(period)) {
      setLoadError({ message: "Choose a plan and a billing period to continue.", code: "MISSING" });
      setLoading(false);
      return null;
    }
    try {
      const q = await fanMembershipApi.preview({
        plan_code: planCode,
        billing_period: period,
        ...(artistSlug ? { artist_slug: artistSlug } : {}),
        ...(expectedPrice !== null && expectedPrice !== undefined ? { expected_price: expectedPrice } : {}),
        ...(addr && addressPayload(addr) ? { billing_address: addressPayload(addr) } : {}),
      });
      setQuote(q);
      // The address the server used (the one sent, or the one saved for this fan) is what the tax on screen is for.
      if (q.billing_address) {
        setQuotedFor(addressKey(addressFrom(q.billing_address)));
        if (!prefilled.current) { prefilled.current = true; setAddress(addressFrom(q.billing_address)); }
      } else {
        setQuotedFor("");
      }
      setLoadError(null);
      if (q.price_changed) setPriceNotice({ from: q.previous_price, to: q.amount });
      return q;
    } catch (e) {
      setLoadError({ message: apiErrorMessage(e, "The checkout summary could not be loaded. Please try again."), code: apiErrorCode(e) });
      return null;
    } finally {
      setLoading(false);
    }
  }, [planCode, period, artistSlug]);

  useEffect(() => {
    setLoading(true);
    loadQuote(seenPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadQuote]);

  const addressEnabled = Boolean(quote && quote.billing_address_enabled);
  const typedKey = addressKey(address);
  const taxPending = addressEnabled && Boolean(typedKey) && typedKey !== quotedFor;
  useEffect(() => {
    if (!addressEnabled || !typedKey || typedKey === quotedFor) return undefined;
    const timer = setTimeout(() => { loadQuote(null, address); }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedKey, addressEnabled, quotedFor]);

  // Asks the server to check the payment with Stripe. Returns true when the membership is now active.
  const confirmWithServer = async (intentId, stripeMessage) => {
    try {
      const result = await fanMembershipApi.confirm({ payment_intent_id: intentId });
      if (result.status === "active") {
        setWaiting(null);
        setDone(result);
        return true;
      }
      setWaiting({ intentId, message: result.message });
      return false;
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "PAYMENT_FAILED" || code === "PAYMENT_CANCELLED" || stripeMessage) {
        setWaiting(null);
        setFailure(stripeMessage ? `${stripeMessage} Your membership is not active and you have not been charged.` : apiErrorMessage(e));
      } else {
        setWaiting({ intentId, message: apiErrorMessage(e, "We could not confirm your payment yet.") });
      }
      return false;
    }
  };

  // The price changed between the plans page, this page and the payment: show the new price and make the fan review it.
  const handlePriceChanged = async (e) => {
    const before = quote ? quote.total_amount : null;
    const fresh = await loadQuote(null);
    setPriceNotice({ from: before !== null ? before : e?.response?.data?.details?.previous_total, to: fresh ? fresh.total_amount : e?.response?.data?.details?.current_total, total: true });
    setFailure("");
  };

  const begin = () => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setFailure("");
    setWaiting(null);
    return true;
  };
  const end = () => {
    busyRef.current = false;
    setBusy("");
  };

  const pay = async (event) => {
    event.preventDefault();
    if (priceNotice || !quote) return;
    if (!stripe || !elements) { setFailure("The payment form is still loading. Please try again in a moment."); return; }
    if (!name.trim()) { setNameError("Enter the name on the card."); return; }
    const billing = addressEnabled ? addressPayload(address) : undefined;
    if (addressEnabled && !billing) { setShowAddressErrors(true); return; }
    if (taxPending) return;
    if (!begin()) return;
    setNameError("");
    setBusy("pay");
    try {
      let started;
      try {
        started = await fanMembershipApi.start(withArtist({ plan_code: planCode, billing_period: period, expected_total: quote.total_amount, ...(billing ? { billing_address: billing } : {}) }));
      } catch (e) {
        if (apiErrorCode(e) === "PRICE_CHANGED") await handlePriceChanged(e);
        else setFailure(apiErrorMessage(e, "The payment could not be started. Please try again."));
        return;
      }
      if (started.payment_status === "paid") { setDone(started); return; } // this payment had already gone through
      if (started.payment_status === "processing") { await confirmWithServer(started.payment_intent_id, ""); return; }
      const { error, paymentIntent } = await stripe.confirmCardPayment(started.client_secret, {
        payment_method: { card: elements.getElement(CardNumberElement), billing_details: { name: name.trim() } },
      });
      // Whatever the card result, the server decides: it reads the payment from Stripe before activating anything.
      await confirmWithServer(started.payment_intent_id, error ? error.message : paymentIntent && paymentIntent.status !== "succeeded" ? "The payment was not completed." : "");
    } finally {
      end();
    }
  };

  // Test environments only. The server refuses it everywhere else.
  const simulate = async (outcome) => {
    if (priceNotice || !quote) return;
    const billing = addressEnabled ? addressPayload(address) : undefined;
    if (addressEnabled && !billing) { setShowAddressErrors(true); return; }
    if (taxPending || !begin()) return;
    setBusy(outcome);
    try {
      const result = await fanMembershipApi.simulate(withArtist({ plan_code: planCode, billing_period: period, outcome, expected_total: quote.total_amount, ...(billing ? { billing_address: billing } : {}) }));
      setDone(result);
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "PRICE_CHANGED") await handlePriceChanged(e);
      else if (code === "PAYMENT_DECLINED") setFailure(apiErrorMessage(e, "Payment declined. Please try again."));
      else setFailure(apiErrorMessage(e, "The simulated payment could not be processed."));
    } finally {
      end();
    }
  };

  const checkAgain = async () => {
    if (!waiting || !begin()) return;
    setBusy("pay");
    try { await confirmWithServer(waiting.intentId, ""); } finally { end(); }
  };

  const working = busy !== "";
  let body;
  if (loading) {
    body = <div className="flex justify-center py-24" role="status" aria-label="Loading checkout"><Loading /></div>;
  } else if (loadError && !quote) {
    body = (
      <div className="max-w-md mx-auto text-center py-16">
        <p className="text-gray-700 mb-5" role="alert">{loadError.message}</p>
        <div className="flex justify-center gap-3">
          {loadError.code !== "MISSING" && loadError.code !== "PLAN_NOT_FOUND" && loadError.code !== "ALREADY_SUBSCRIBED" && (
            <button type="button" onClick={() => { setLoading(true); loadQuote(seenPrice); }} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
          )}
          {loadError.code === "ALREADY_SUBSCRIBED" && (
            <Link to="/fan-membership/details" className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>View membership details</Link>
          )}
          <Link to={plansLink} className="rounded-full font-semibold border-2 px-6 py-2.5" style={{ borderColor: ACCENT, color: ACCENT }}>Back to plans</Link>
        </div>
      </div>
    );
  } else if (done) {
    const sub = done.subscription;
    body = (
      <div className="max-w-lg mx-auto text-center py-12" role="status">
        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-green-50">
          <svg viewBox="0 0 24 24" className="w-9 h-9" fill="none" aria-hidden="true">
            <path d="M5 12.5l4.5 4.5L19 7.5" stroke={ACCENT} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="mt-5 text-3xl font-bold text-gray-900">Subscription activated successfully.</h1>
        {sub && (
          <div className="mt-6 rounded-2xl border border-gray-200 p-5 text-left">
            <Row label="Plan" value={sub.plan_name} />
            <Row label="Billing" value={sub.billing_label || (sub.billing_period === "annual" ? "Billed annually" : "Billed monthly")} />
            <Row label="Amount paid" value={money(sub.total_amount, sub.currency)} />
            <Row label="Active until" value={formatDate(sub.current_period_end)} />
            <Row label="Renewal" value={sub.next_renewal_date ? formatDate(sub.next_renewal_date) : "Does not renew automatically"} />
            {sub.payment_mode === "simulated" && <Row label="Payment" value="Simulated (test)" />}
          </div>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/fan-membership/details" className="rounded-full font-semibold text-white px-8 py-3" style={{ backgroundColor: ACCENT }}>View membership details</Link>
          {sub?.id && <Link to={receiptLink("membership", sub.id)} data-testid="view-receipt" className="rounded-full font-semibold border-2 px-8 py-3" style={{ borderColor: ACCENT, color: ACCENT }}>View receipt</Link>}
          <button type="button" onClick={() => navigate("/home")} className="rounded-full font-semibold border-2 px-8 py-3" style={{ borderColor: ACCENT, color: ACCENT }}>Start listening</button>
          {artistSlug && <Link to={`/artists/${encodeURIComponent(artistSlug)}`} className="rounded-full font-semibold border-2 px-8 py-3" style={{ borderColor: ACCENT, color: ACCENT }}>Back to the artist</Link>}
        </div>
      </div>
    );
  } else {
    body = (
      <div className="max-w-4xl mx-auto">
        <Link to={plansLink} className="text-sm font-semibold underline" style={{ color: ACCENT }}>← Back to plans</Link>
        <h1 className="mt-3 text-3xl font-bold text-gray-900">Checkout</h1>

        {priceNotice && (
          <div className="mt-6">
            <Notice
              tone="info"
              title="The price has changed"
              actions={<button type="button" onClick={() => setPriceNotice(null)} className="rounded-full font-semibold text-white px-5 py-2 text-sm" style={{ backgroundColor: ACCENT }}>I've reviewed the new price</button>}
            >
              {priceNotice.from !== null && priceNotice.from !== undefined ? `It was ${money(priceNotice.from, quote.currency)} and is now ${money(priceNotice.to, quote.currency)}.` : `It is now ${money(priceNotice.to, quote.currency)}.`}{" "}
              Please review the details below before you subscribe.
            </Notice>
          </div>
        )}

        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <section aria-label="Order summary" className="rounded-3xl border border-gray-200 p-6 h-fit">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{quote.plan.name}</h2>
              {quote.plan.label && <span className="rounded-full text-white text-xs font-semibold px-3 py-1" style={{ backgroundColor: ACCENT }}>{quote.plan.label}</span>}
            </div>
            <p className="mt-1 text-sm font-semibold" style={{ color: ACCENT }}>{quote.billing_label}</p>
            <ul className="mt-3 space-y-1 text-sm text-gray-600 list-disc pl-5">
              {quote.plan.benefits.map((b) => <li key={b}>{b}</li>)}
            </ul>
            <div className="mt-5">
              <Row label="Plan" value={quote.plan.name} />
              <Row label="Billing period" value={quote.billing_label} />
              <Row label="Subscription price" value={money(quote.amount, quote.currency)} />
              {quote.show_savings && <Row label="You save" value={`${Math.round(quote.savings_percent)}% compared with paying monthly`} />}
              <Row label={quote.tax_percent ? `Taxes (${quote.tax_percent}%)` : "Taxes"} value={taxPending ? "Updating…" : quote.billing_address_required ? "Enter your billing address" : money(quote.tax_amount, quote.currency)} />
              <Row label="Total payable today" value={money(quote.total_amount, quote.currency)} strong />
            </div>
            <div className="mt-5 rounded-2xl bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
              <p className="font-semibold text-gray-900">{quote.charge_note}</p>
              {quote.renewal_terms.map((t) => <p key={t}>{t}</p>)}
            </div>
          </section>

          <form onSubmit={pay} className="rounded-3xl border border-gray-200 p-6" noValidate aria-label="Card payment">
            <h2 className="text-xl font-bold text-gray-900">Pay by card</h2>

            {failure && (
              <div className="mt-4">
                <Notice
                  title="Payment unsuccessful"
                  actions={<>
                    <span className="text-sm">You can try again, or</span>
                    <Link to={plansLink} className="text-sm font-semibold underline">go back to the plans</Link>
                  </>}
                >
                  {failure}
                </Notice>
              </div>
            )}
            {waiting && (
              <div className="mt-4">
                <Notice
                  tone="info"
                  title="Your payment is not confirmed yet"
                  actions={<button type="button" onClick={checkAgain} disabled={working} className="text-sm font-semibold underline disabled:opacity-60">Check payment status</button>}
                >
                  {waiting.message} Your membership is not active until the payment is confirmed. Please do not pay again.
                </Notice>
              </div>
            )}

            {addressEnabled && <BillingAddressFields value={address} onChange={setAddress} showAll={showAddressErrors} disabled={working} idPrefix="fm-billing" />}

            <label className="block text-sm text-gray-600 mt-5 mb-1" htmlFor="fm-card-name">Name on card</label>
            <input
              id="fm-card-name"
              type="text"
              autoComplete="cc-name"
              value={name}
              onChange={(e) => setName(clean.name(e.target.value).slice(0, 80))}
              aria-invalid={Boolean(nameError)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5"
              placeholder="Name"
            />
            {nameError && <p className="text-sm text-red-600 mt-1" role="alert">{nameError}</p>}

            <label className="block text-sm text-gray-600 mt-4 mb-1">Card number</label>
            <div className="border border-gray-300 rounded-lg px-3 py-3 bg-white"><CardNumberElement options={ELEMENT_OPTIONS} /></div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Expiry date</label>
                <div className="border border-gray-300 rounded-lg px-3 py-3 bg-white"><CardExpiryElement options={ELEMENT_OPTIONS} /></div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">CVC</label>
                <div className="border border-gray-300 rounded-lg px-3 py-3 bg-white"><CardCvcElement options={ELEMENT_OPTIONS} /></div>
              </div>
            </div>

            <button
              type="submit"
              disabled={working || !stripe || Boolean(waiting) || Boolean(priceNotice) || taxPending}
              aria-busy={busy === "pay"}
              className="mt-6 w-full rounded-full font-semibold text-white py-3 disabled:opacity-60"
              style={{ backgroundColor: ACCENT }}
            >
              {busy === "pay" ? "Processing payment…" : `Confirm & Subscribe · ${money(quote.total_amount, quote.currency)}`}
            </button>
            <p className="mt-3 text-xs text-gray-500 text-center">Your card is processed securely by Stripe. Your membership starts only after the payment is confirmed.</p>

            {quote.simulation_available && (
              <div className="mt-6 rounded-2xl border border-dashed border-gray-300 p-4" aria-label="Payment simulation (testing only)">
                <p className="text-sm font-semibold text-gray-800">Testing only</p>
                <p className="text-xs text-gray-500 mt-1">No card is charged. These buttons exist only in development, staging and QA.</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <button type="button" onClick={() => simulate("success")} disabled={working || Boolean(priceNotice) || taxPending} aria-busy={busy === "success"} className="rounded-full border-2 px-4 py-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: ACCENT, color: ACCENT }}>
                    {busy === "success" ? "Simulating…" : "Simulate: Success"}
                  </button>
                  <button type="button" onClick={() => simulate("declined")} disabled={working || Boolean(priceNotice) || taxPending} aria-busy={busy === "declined"} className="rounded-full border-2 border-red-300 text-red-700 px-4 py-2 text-sm font-semibold disabled:opacity-60">
                    {busy === "declined" ? "Simulating…" : "Simulate: Declined"}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return <FanMembershipShell>{body}</FanMembershipShell>;
}
